import { Tabs } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import { ContentContainer } from '../../../components/custom_components/ContentContainer'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { PerProfileHeader } from '../../../components/custom_components/Profile/ProfileHeader'
import useResignedEmployeePublicProfile from '../../../graphql/profiles/useResignedEmployeePublicProfile'
import { useCollapse } from '../../../routes/collapse-context'
import { EmployeeDisplayTinyInfo } from '../../../types'
import { EmployeeTargetsContainer } from '../../targets/EmployeeTargetsContainer'
import { PublicMeetingsTab } from '../tabs/PublicMeetingsTab'
import { ResignedEmployeeOverviewTab } from './ResignedEmployeeOverviewTab'

const { TabPane } = Tabs

export type ProfileTab = 'overview' | 'personalData' | 'targets' | 'meetings' | 'contract' | 'notes'

interface ResignedEmployeeProfilePage {
  employeeId: string
}
export const ResignedEmployeeProfilePage: FC<ResignedEmployeeProfilePage> = ({ employeeId }) => {
  const [activeKey, setActiveKey] = useState('1')
  const collapsed = useCollapse()

  const { data, error, loading } = useResignedEmployeePublicProfile(employeeId)
  const { t } = useTranslation()

  if (loading || error || !data || !data.resignedEmployeePublicProfile) {
    return (
      <ContentContainer collapsed={collapsed}>
        <BackgroundBox r height="fullPage">
          <NewLoadingAndErrors
            isLoading={loading}
            error={error}
            hasNoData={!data || !data.resignedEmployeePublicProfile}
            showSpinner
            t={t}
          />
        </BackgroundBox>
      </ContentContainer>
    )
  }

  const employee: EmployeeDisplayTinyInfo = {
    id: data.resignedEmployeePublicProfile.employeeId,
    firstName: data.resignedEmployeePublicProfile.chosenFirstName
      ? data.resignedEmployeePublicProfile.chosenFirstName
      : data.resignedEmployeePublicProfile.legalFirstName,
    lastName: data.resignedEmployeePublicProfile.chosenLastName
      ? data.resignedEmployeePublicProfile.chosenLastName
      : data.resignedEmployeePublicProfile.legalLastName,
    avatarUrl: data.resignedEmployeePublicProfile.avatarUrl,
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <div data-e2e-public-profile-page>
        <Tabs
          activeKey={activeKey}
          renderTabBar={() => (
            <PerProfileHeader profile={data.resignedEmployeePublicProfile}>
              <Tabs onChange={(key) => setActiveKey(key)}>
                <TabPane tab={t('profile.tabs.overview')} key="1" data-e2e-public-profile-tab="resigned_overview"></TabPane>
                <TabPane tab={t('profile.tabs.targets')} key="2" data-e2e-public-profile-tab="resigned_targets"></TabPane>
                <TabPane tab={t('profile.tabs.meetings')} key="3" data-e2e-public-profile-tab="resigned_meetings"></TabPane>
              </Tabs>
            </PerProfileHeader>
          )}
        >
          <TabPane key="1">
            <BackgroundBox r height="profileView">
              <ResignedEmployeeOverviewTab profile={data.resignedEmployeePublicProfile} />
            </BackgroundBox>
          </TabPane>

          <TabPane key="2">
            <BackgroundBox r height="profileView" color="transparent" noPadding noShadow>
              <EmployeeTargetsContainer
                employee={employee}
                publicProfile
                employeeEditable={data.resignedEmployeePublicProfile.editable}
                resignedView
                view="ResignedEmployeeProfilePage"
                employeeSkillStatus={null}
              />
            </BackgroundBox>
          </TabPane>

          <TabPane key="3">
            <BackgroundBox r height="profileView" color="transparent" noPadding noShadow>
              <PublicMeetingsTab
                employeeId={employeeId}
                employee={employee}
                editable={data.resignedEmployeePublicProfile.editable}
                resignedView
              />
            </BackgroundBox>
          </TabPane>
        </Tabs>
      </div>
    </ContentContainer>
  )
}
